import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import { Box } from "@mui/material";
import type { FC } from "react";
import { NotificationType, type RequestOrigins, type ShiftRequest } from "../../../utils/models";
import { FactList } from "../../UIToolkit/molecules/FactList";
import { ExplainerCard, type ExplainerCardWidth } from "../../UIToolkit/Panels/ExplainerCard";
import { RequestOriginCauseList } from "./RequestOriginCauseList";
import { RequestOriginHppdMeter } from "./RequestOriginHppdMeter";
import { cardTitle, eyebrow, sentence, slotsFooter } from "./requestOriginCopy";
import { hppdRationaleOf, openingFacts } from "./requestOriginFacts";
import { ORIGIN_GROUP_TONE, ORIGIN_KIND_ICON, type RequestOriginViewLevel } from "./requestOriginMeta";
import { useOriginCopyContext } from "./useOriginCopyContext";
import { useTrackOriginViewed } from "./useTrackOriginViewed";

type Props = {
	request: ShiftRequest;
	origins: RequestOrigins;
	level: RequestOriginViewLevel;
	width: ExplainerCardWidth;
};

/**
 * "Why this shift was posted". One cause: its sentence (with the numbers behind an HPPD auto-post) and its facts.
 * Several: the flow that opened the slots, step by step, and what it adds up to. The same card in the hover card and
 * the phone sheet.
 */
export const RequestOriginCard: FC<Props> = ({ request, origins, level, width }) => {
	const copy = useOriginCopyContext();
	useTrackOriginViewed(request, origins, level);
	const [first] = origins.openings;
	const testId = `request-origin-card-${request.id}`;
	const header = { dataTestId: testId, width, eyebrow: eyebrow(origins, copy), title: cardTitle(origins, copy) };

	if (origins.openings.length > 1) {
		const stillOpen = request.request_type === NotificationType.OpenShift && "needed_nurses" in request ? request.needed_nurses : null;
		return (
			<ExplainerCard
				{...header}
				icon={<TimelineOutlinedIcon />}
				tone={ORIGIN_GROUP_TONE[first.group]}
				footer={stillOpen === null ? undefined : slotsFooter(origins, stillOpen, copy)}
			>
				<RequestOriginCauseList request={request} origins={origins} copy={copy} />
			</ExplainerCard>
		);
	}

	const Icon = ORIGIN_KIND_ICON[first.kind];
	const hppd = hppdRationaleOf(first);
	return (
		<ExplainerCard {...header} icon={<Icon />} tone={ORIGIN_GROUP_TONE[first.group]} facts={<FactList items={openingFacts(first, copy)} />}>
			<Box component="p" sx={{ m: 0 }}>
				{sentence(first, request, false, copy)}
			</Box>
			{hppd !== null && (
				<Box sx={{ mt: 1.25 }}>
					<RequestOriginHppdMeter rationale={hppd} dataTestId={`${testId}-meter`} />
				</Box>
			)}
		</ExplainerCard>
	);
};
